import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FiRefreshCw } from "react-icons/fi";
import type { DepartmentReportDocument } from "@features/department/api/departmentReportDocumentApi";
import { changeDepartmentReportDocumentOrigin } from "@features/department/api/departmentReportDocumentApi";
import type { OriginScope } from "@features/report/template/types/reportTemplate";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import {
  DOCUMENT_CATEGORY_ORIGIN_CREATE_CODES,
  getDocumentCategoryCreateCode,
  getDocumentCategoryOriginUpdateCode,
} from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import type { ErrorResponse } from "@core/types/errorResponse";

const ORIGIN_SCOPE_OPTIONS: { value: OriginScope; label: string }[] = [
  { value: "CHURCH", label: "Church" },
  { value: "DISTRICT", label: "District" },
  { value: "NATION", label: "Nation" },
];

type Props = {
  show: boolean;
  onHide: () => void;
  nationId: number;
  departmentId: number;
  document: DepartmentReportDocument | null;
  onChanged?: () => void;
};

export default function ChangeDepartmentReportDocumentOriginModal({
  show,
  onHide,
  nationId,
  departmentId,
  document,
  onChanged,
}: Props) {
  const [selectedScope, setSelectedScope] = useState<OriginScope | "">("");
  const [busy, setBusy] = useState(false);
  const showError = useApiError();
  const showValidation = useErrorModalStore((s) => s.showValidation);

  const fromOriginCode = document?.origin_scope
    ? getDocumentCategoryOriginUpdateCode(document.origin_scope)
    : "";

  const { hasPermission } = usePermissions({
    scopeType: "NATION",
    scopeId: nationId || undefined,
    actionCodes: [...DOCUMENT_CATEGORY_ORIGIN_CREATE_CODES, fromOriginCode].filter(Boolean),
  });

  const availableScopes = ORIGIN_SCOPE_OPTIONS.filter((opt) =>
    Boolean(hasPermission(getDocumentCategoryCreateCode(opt.value)))
  );

  useEffect(() => {
    setSelectedScope(document?.origin_scope ?? "");
  }, [document, show]);

  const handleClose = () => {
    setSelectedScope("");
    onHide();
  };

  const handleSubmit = async () => {
    const messages: string[] = [];
    if (!document) messages.push("No document category selected.");
    if (!selectedScope) messages.push("Please select who will upload this document category.");
    if (selectedScope === document?.origin_scope) {
      messages.push("No change detected. Please select a different scope.");
    }
    if (messages.length > 0) {
      showValidation(messages);
      return;
    }

    setBusy(true);
    try {
      const response = await changeDepartmentReportDocumentOrigin(
        nationId,
        departmentId,
        document!.department_report_document_id,
        selectedScope as OriginScope
      );
      if (response.isSuccess()) {
        showSuccess(response.getData()?.message ?? "Origin scope updated successfully.");
        handleClose();
        onChanged?.();
      } else {
        showError(response.getError() as ErrorResponse);
      }
    } catch (error) {
      showError(error as ErrorResponse);
    } finally {
      setBusy(false);
    }
  };

  const currentScopeLabel = document?.origin_scope
    ? ORIGIN_SCOPE_OPTIONS.find((o) => o.value === document.origin_scope)?.label || document.origin_scope
    : "Not set";

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      data-testid="change-document-origin-scope-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-semibold">
          <FiRefreshCw className="me-2" aria-hidden />
          Change Who Uploads This
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-3 pb-4">
        <Alert variant="info" className="mb-3">
          <strong>Current setting:</strong> {currentScopeLabel}
        </Alert>
        {document ? (
          <div className="mb-3 p-3 bg-light rounded">
            <div className="fw-semibold">{document.name}</div>
            {document.description ? (
              <div className="text-muted small mt-1">{document.description}</div>
            ) : null}
          </div>
        ) : null}
        <Form.Group className="mb-3" controlId="documentOriginScope">
          <Form.Label className="fw-semibold">
            To be completed by <span className="text-danger">*</span>
          </Form.Label>
          {availableScopes.length === 0 ? (
            <Alert variant="warning" className="mb-0">
              No origin scopes are available based on current permissions.
            </Alert>
          ) : (
            <Form.Select
              value={selectedScope}
              onChange={(e) => setSelectedScope(e.target.value as OriginScope | "")}
              disabled={busy}
            >
              <option value="">Select who will upload this…</option>
              {availableScopes.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>
        <div className="d-flex justify-content-end gap-2 mt-4">
          <Button variant="outline-secondary" onClick={handleClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={busy || !selectedScope || selectedScope === document?.origin_scope}
          >
            {busy ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Updating…
              </>
            ) : (
              "Save Change"
            )}
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
